import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UsersRound, ShieldCheck } from "lucide-react";

export default function TeamAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setError("Missing invite token"); return; }
    api.get(`/team/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(formatError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || password.length < 6) { toast.error("Add your name and a 6+ character password"); return; }
    setBusy(true);
    try {
      await api.post("/team/accept", { token, name, password });
      await refresh?.();
      toast.success("You're in!");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-shell flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-lg tracking-tight flex items-center gap-2" data-testid="team-brand">
            <span className="w-6 h-6 rounded-full grad-band" /> CPG Institute
          </Link>
          <Link to="/" className="text-sm hover:text-primary" data-testid="team-accept-signin">Already have an account?</Link>
        </div>
      </header>

      <main className="flex-1 grid place-items-center p-6">
        <div className="surface w-full max-w-md p-8" data-testid="team-accept-card">
          <p className="chip-amber inline-flex items-center gap-1"><UsersRound className="w-3 h-3" /> Team seat invitation</p>
          {error ? (
            <div className="mt-6 text-sm text-destructive" data-testid="team-accept-error">{error}</div>
          ) : !invite ? (
            <div className="mt-6 text-sm text-muted-foreground">Loading invite…</div>
          ) : (
            <>
              <h1 className="font-display text-3xl mt-4" data-testid="team-accept-title">
                Join {invite.owner_name}'s workspace
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                They've added a seat for <span className="font-semibold">{invite.invite_email}</span>. Set up your login below to accept.
              </p>
              <form onSubmit={submit} className="mt-6 grid gap-3">
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Your name
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-accept-name"
                    className="mt-1 w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm" />
                </label>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Password (6+ characters)
                  <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" data-testid="team-accept-password"
                    className="mt-1 w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm" />
                </label>
                <button type="submit" disabled={busy} data-testid="team-accept-submit"
                  className="btn-primary mt-2 justify-center">
                  {busy ? "Joining…" : "Accept my seat"}
                </button>
                <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                  Your access mirrors the account owner's plan. Billing stays with them.
                </p>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
